<template>
  <!-- Compact single-order card for the customer summary overlay. Same order shape and helpers
       as the list rows, but stacked instead of columned: the list grid needs ~750px to lay out
       and the summary renders it inside a half-width card. Kept as its own block rather than
       threaded through the conditionals below, so the orders drawer is unaffected. -->
  <div
    v-if="props.mode === 'summary'"
    class="orderItem--summary"
    @click.prevent="showOrderPreview(order.id)"
  >
    <div class="orderItem--summary__header">
      <span class="orderItem--summary__number">#{{ order.number }}</span>
      <span class="text-color-neutral text-size-tiny">{{
        formatISODateToDDMMYYYY(order.placed_at)
      }}</span>
      <sl-tag
        size="small"
        :variant="getTagVariantForFulfilmentStatus(order.fulfillment_status)"
        >{{ $t(`fulfillment.status.${summaryStatus}`) }}</sl-tag
      >
    </div>
    <div v-for="lineItem in summaryItems" :key="lineItem.id" class="orderItem--summary__item">
      <div class="orderItem--summary__image">
        <SanityImage
          v-if="lineItem?.image_url"
          loading="lazy"
          :class="lineItem.metadata.type"
          :src="`${lineItem.image_url}?h=80&dpr=2&q=70`"
        />
      </div>
      <div class="orderItem--summary__info">
        <div class="orderItem--summary__title">
          <template v-if="lineItem.item?.metadata?.title">
            {{ lineItem.item.metadata.title[deviceStore.settings.language] }}</template
          >
          <template v-else-if="lineItem.metadata?.title">
            {{ lineItem.metadata.title[deviceStore.settings.language] }}</template
          >
        </div>
        <div class="text-color-neutral text-size-tiny">
          <template v-if="lineItem.item?.metadata?.colour_name">{{
            lineItem.item.metadata.colour_name
          }}</template>
          <template v-if="getConfigSummary(lineItem)">
            <br v-if="lineItem.item?.metadata?.colour_name" />{{ getConfigSummary(lineItem) }}
          </template>
        </div>
      </div>
    </div>
    <div class="orderItem--summary__footer">
      <span class="text-color-neutral">{{ $t('globals.total') }}</span>
      <div class="orderItem--summary__amount">
        <CurrencyFormatter :value="order.total_amount_float" :currency="order.currency_code" />
        <span class="text-size-tiny" :class="getPaymentColour(order.payment_status)">{{
          $t(`payment_status.${order.payment_status}`)
        }}</span>
      </div>
    </div>
  </div>
  <template v-else>
  <div v-if="props.orderIndex === 0 && props.mode !== 'minimal'">
    <div class="orderItem text-color-neutral" :class="`orderItem--${mode}`">
      <div>{{ $t('globals.date') }}</div>
      <div v-if="['overview', 'openCart'].includes(props.mode)">{{ $t('shipment.customer') }}</div>
      <div v-if="props.mode !== 'minimal'">
        {{ $t('promotions.table.header.status') }} / {{ $t('shipment.table.header.sku') }}
      </div>
      <div>{{ $t('globals.employeeShort') }}</div>
      <div>{{ $t('globals.total') }}</div>
      <div v-if="['overview', 'openCart'].includes(props.mode)">
        {{ $t('afterSales.columnAction') }}
      </div>
    </div>
  </div>
  <div
    class="orderItem"
    :class="`orderItem--${mode}`"
    @click.prevent="!['minimal', 'openCart'].includes(props.mode) && showOrderPreview(order.id)"
  >
    <div class="orderItem__date">
      <span
        :class="{
          'cursor--pointer text-underline': !['minimal', 'openCart'].includes(props.mode)
        }"
        >#{{ order.number }}</span
      ><br />
      <span class="text-color-neutral text-size-tiny">{{
        formatISODateToDDMMYYYYWithTime(
          props.mode === 'openCart' ? order.created_at : order.placed_at
        )
      }}</span>
      <span v-if="getAsTag(props.order)" class="text-color-neutral text-size-tiny"
        ><br />{{ $t(`fulfillment.status.${getAsTag(props.order)}`) }}</span
      >
    </div>
    <div v-if="['overview', 'openCart'].includes(props.mode)" class="orderItem__customer">
      <template v-if="order.customer.metadata.first_name">
        {{ order.customer.metadata.first_name[0] }}.
        {{ order.customer.metadata.last_name }}</template
      ><template v-else>{{ $t('globals.guest') }}</template>
    </div>

    <div v-if="props.mode !== 'minimal'" class="orderItem__items">
      <template v-if="props.mode === 'openCart'">
        <div v-for="lineItem in openCartItems" :key="lineItem.id" class="orderItem__item">
          <div class="orderItem__item-status">
            <sl-tag size="small" variant="primary-orange">{{
              $t('dayOverview.statusPending')
            }}</sl-tag>
          </div>
          <div class="orderItem__item-image">
            <SanityImage
              v-if="lineItem?.image_url"
              loading="lazy"
              :class="lineItem.metadata.type"
              :src="`${lineItem.image_url}?h=80&dpr=2&q=70`"
            />
          </div>
          <div class="orderItem__item-info">
            <div class="orderItem__item-info-title">
              <template v-if="lineItem.item?.metadata?.title">
                {{ lineItem.item.metadata.title[deviceStore.settings.language] }}</template
              >
              <template v-else-if="lineItem.metadata?.title">
                {{ lineItem.metadata.title[deviceStore.settings.language] }}</template
              >
            </div>
            <div class="text-color-neutral text-size-tiny">
              <template v-if="lineItem.sku_code">{{ lineItem.sku_code }}</template>
              <template v-if="lineItem.item.metadata?.colour_name"
                ><br />{{ lineItem.item.metadata.colour_name }}</template
              >
              <template v-if="lineItem.metadata.type === 'gift_card'">{{
                lineItem.item?.code
              }}</template>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <div v-for="shipment in orderShipments" :key="shipment.id" class="orderItem__shipment">
          <div
            v-for="lineItem in getVisibleItems(shipment.line_items)"
            :key="lineItem.id"
            class="orderItem__item"
          >
            <div class="orderItem__item-status">
              <sl-tag v-if="getAsTag(shipment)" size="small" variant="primary-danger">{{
                $t(`fulfillment.status.${getAsTag(shipment)}`)
              }}</sl-tag>
              <sl-tag v-else-if="hasReturn(lineItem)" size="small" variant="primary-danger">{{
                $t(`fulfillment.status.${lineItem?.return_line_items[0]?.return?.reference}`)
              }}</sl-tag>

              <sl-tag
                v-else-if="awaitingPayment(order) || order.status === 'placed'"
                size="small"
                variant="primary-orange"
                >{{ $t('payment_status.awaiting_payment') }}</sl-tag
              >
              <sl-tag
                v-else-if="
                  shipment?.shipping_method?.reference === 'PICKUP' &&
                  ['ready_to_ship'].includes(shipment?.status)
                "
                size="small"
                variant="primary-blue"
                >{{ $t('fulfillment.status.ready_to_pickup') }}</sl-tag
              >
              <sl-tag
                v-else-if="
                  (shipment?.shipping_method?.reference === 'PICKUP' &&
                    ['shipped'].includes(shipment?.status)) ||
                  ['not_required'].includes(order.fulfillment_status)
                "
                size="small"
                variant="primary-green"
                >{{ $t('fulfillment.status.picked_up') }}</sl-tag
              >
              <sl-tag
                v-else
                size="small"
                :variant="
                  getTagVariantForFulfilmentStatus(
                    shipment?.status ? shipment?.status : order.fulfillment_status
                  )
                "
                >{{
                  $t(
                    `fulfillment.status.${
                      shipment?.status ? shipment?.status : order.fulfillment_status
                    }`
                  )
                }}</sl-tag
              >
            </div>
            <div class="orderItem__item-image">
              <SanityImage
                v-if="lineItem?.image_url"
                loading="lazy"
                :class="lineItem.metadata.type"
                :src="`${lineItem.image_url}?h=80&dpr=2&q=70`"
              />
            </div>
            <div class="orderItem__item-info">
              <div class="orderItem__item-info-title">
                <template v-if="lineItem.item?.metadata?.title">
                  {{ lineItem.item.metadata.title[deviceStore.settings.language] }}</template
                >
                <template v-else-if="lineItem.metadata?.title">
                  {{ lineItem.metadata.title[deviceStore.settings.language] }}</template
                >
              </div>
              <div class="text-color-neutral text-size-tiny">
                <template v-if="lineItem.sku_code">{{ lineItem.sku_code }}</template>
                <template v-if="lineItem.item.metadata?.colour_name"
                  ><br />{{ lineItem.item.metadata.colour_name }}</template
                >
                <template v-if="lineItem.metadata.type === 'gift_card'">{{
                  lineItem.item?.code
                }}</template>
              </div>
            </div>
          </div>
        </div>

        <div v-for="lineItem in itemWithNoShipment" :key="lineItem.id" class="orderItem__item">
          <div class="orderItem__item-status">
            <sl-tag
              v-if="
                hasReturn(lineItem) && lineItem?.return_line_items[0]?.return?.type !== 'insurance'
              "
              size="small"
              variant="primary-danger"
              >{{
                $t(`fulfillment.status.${lineItem?.return_line_items[0]?.return?.reference}`)
              }}</sl-tag
            >
            <sl-tag
              v-else-if="awaitingPayment(order) || order.status === 'placed'"
              size="small"
              variant="primary-orange"
              >{{ $t('payment_status.awaiting_payment') }}</sl-tag
            >
            <template v-else>
              <sl-tag
                v-if="order.status === 'cancelled' && !order.approved_at"
                size="small"
                variant="primary-danger"
                >{{ $t('fulfillment.status.cancelled') }}</sl-tag
              >
              <sl-tag
                v-else-if="isMissingShipment(lineItem)"
                size="small"
                variant="primary-danger"
                >{{ $t('fulfillment.status.missingShipment') }}</sl-tag
              >
              <sl-tag v-else size="small" variant="primary-green">{{
                $t('fulfillment.status.picked_up')
              }}</sl-tag>
            </template>
          </div>
          <div class="orderItem__item-image">
            <SanityImage
              v-if="lineItem?.image_url"
              loading="lazy"
              :class="lineItem.metadata.type"
              :src="`${lineItem.image_url}?h=80&dpr=2&q=70`"
            />
          </div>
          <div class="orderItem__item-info">
            <div class="orderItem__item-info-title">
              <template v-if="lineItem.item?.metadata?.title">
                {{ lineItem.item.metadata.title[deviceStore.settings.language] }}</template
              >
              <template v-else-if="lineItem.metadata?.title">
                {{ lineItem.metadata.title[deviceStore.settings.language] }}</template
              >
            </div>
            <div class="text-color-neutral text-size-tiny">
              <template v-if="lineItem.sku_code">{{ lineItem.sku_code }}</template>
              <template v-if="lineItem.item.metadata?.colour_name"
                ><br />{{ lineItem.item.metadata.colour_name }}</template
              >
              <template v-if="lineItem.metadata.type === 'gift_card'">{{
                lineItem.item?.code
              }}</template>
            </div>
          </div>
        </div>
      </template>
    </div>
    <div class="orderItem__market">
      <sl-avatar
        style="--size: 28px"
        :initials="getInitials(employeeName?.split(' ')[0], employeeName?.split(' ')[1])"
      /><template v-if="!['overview', 'openCart'].includes(props.mode)"
        >&nbsp;{{ order.market.code }}</template
      >
    </div>
    <div class="orderItem__price">
      <CurrencyFormatter
        :value="order.total_amount_float"
        :currency="order.currency_code"
      /><br /><span class="text-size-tiny" :class="getPaymentColour(order.payment_status)">{{
        $t(`payment_status.${order.payment_status}`)
      }}</span>
    </div>
    <div v-if="props.mode === 'overview'" class="orderItem__action">
      <sl-button @click.prevent="showOrderPreview(order.id)">{{
        $t('table.actions.open')
      }}</sl-button>
    </div>
    <div v-if="props.mode === 'openCart'" class="orderItem__action">
      <sl-button @click.prevent="emit('open-cart', order)">{{
        $t('table.actions.openCart')
      }}</sl-button>
    </div>
    <div v-if="props.mode === 'minimal'">
      <sl-tag
        :key="order.fulfillment_status"
        size="medium"
        :variant="getTagVariantForFulfilmentStatus(order.fulfillment_status)"
      >
        {{ capitalizeFirstLetter(order.fulfillment_status?.split('_').join(' ')) }}</sl-tag
      >
    </div>
  </div>
  </template>
</template>

<script setup>
import SanityImage from '@/components/shared/SanityImage.vue'
import '@shoelace-style/shoelace/dist/components/avatar/avatar'
import { formatISODateToDDMMYYYY, formatISODateToDDMMYYYYWithTime } from '@/utils/dateTime'
import CurrencyFormatter from '@/components/shared/CurrencyFormatter/CurrencyFormatter.vue'
import { getTagVariantForFulfilmentStatus } from '@/utils/status.js'
import { useDeviceStore } from '@/stores/device.js'
import { useCustomerStore } from '@/stores/customer.js'
import { useUiStore } from '@/stores/ui.js'
import { sortOrdersItems, awaitingPayment } from '@/utils/order.js'
import { getInitials, capitalizeFirstLetter } from '@/utils/string.js'
import { useCustomer } from '@/composables/useCustomer.js'
import { computed } from 'vue'

const { loadCustomerById } = useCustomer()
const deviceStore = useDeviceStore()
const customerStore = useCustomerStore()
const uiStore = useUiStore()
const props = defineProps({
  order: {
    type: Object,
    default: () => ({})
  },
  orderIndex: {
    type: Number,
    default: 0
  },
  mode: {
    type: String,
    default: 'customer'
  }
})

const emit = defineEmits(['open-cart'])

const CONFIG_SUMMARY_SEPARATOR = ' · '

const orderShipments = computed(() => {
  return getOrderShipments(props.order.shipments)
})

// Pending carts have no shipments — show their line items directly.
const openCartItems = computed(() => {
  return getVisibleItems(props.order.line_items)
})

// Summary mode lists the products directly as well — one card for one order leaves no room for
// the per-shipment breakdown the list rows build, so it wants the same visible-item rule.
const summaryItems = openCartItems

/**
 * The configuration of one product as a single line. ProductView splits config_summary across
 * the pair's line items — the frame step lands on the main item, and only for
 * frame/customMadeFrame, while the lens step (lens type, index, coatings) lands on the lens
 * item, which getVisibleItems() hides. Both share the pair's `reference`, so collect across the
 * group, then re-join every option on one separator so the split is invisible to the reader.
 *
 * @param {Object} lineItem - The visible line item to describe
 * @returns {string} the options of that product, or '' if none are stored
 */
function getConfigSummary(lineItem) {
  const siblings = lineItem.reference
    ? props.order.line_items?.filter(
        (sibling) => sibling.id !== lineItem.id && sibling.reference === lineItem.reference
      )
    : []

  const options = [lineItem, ...(siblings ?? [])]
    .map((item) => item.metadata?.config_summary)
    .filter(Boolean)
    // Each summary is itself a comma-joined list of option names, so split it back apart —
    // otherwise the two sources read as one list in commas and another appended after it.
    .flatMap((summary) => summary.split(',').map((option) => option.trim()))
    .filter(Boolean)

  return [...new Set(options)].join(CONFIG_SUMMARY_SEPARATOR)
}

// An order needing no shipment (gift cards, say) is shown as picked up in the orders list, via
// the shipment branch below. Summary mode tags the order directly, so it maps the same way
// rather than falling through to a `fulfillment.status.not_required` key that does not exist.
const summaryStatus = computed(() => {
  return props.order.fulfillment_status === 'not_required'
    ? 'picked_up'
    : props.order.fulfillment_status
})

// Carts store the employee under `employee_created`; placed orders under `employee_placed`.
const employeeName = computed(() => {
  return props.mode === 'openCart'
    ? props.order.metadata?.employee_created?.name
    : props.order.metadata?.employee_placed?.name
})

const itemWithNoShipment = computed(() => {
  // return all items from order.value.line_items that are not included in any order.value.shipments.line_items.
  const items = props.order.line_items.filter((li) => {
    return !orderShipments.value
      ?.map((s) => s.line_items)
      .flat()
      .some((sli) => sli.id === li.id)
  })

  return getVisibleItems(items)
})

/**
 * Checks if a line item is missing a shipment.
 *
 * @param {Object} lineItem - The line item to check
 * @returns {boolean} true if the line item is missing a shipment, false otherwise
 */
function isMissingShipment(lineItem) {
  return ['lens', 'frame', 'sun', 'customMadeFrame'].includes(lineItem.metadata?.type)
}

/**
 * Filters out shipments with a 'warranty' tag and merges the tags from shipments with a 'warranty' tag
 * to shipments without a 'warranty' tag that have matching line items.
 *
 * @param {Object[]} shipments - Array of shipments
 * @returns {Object[]} - Array of shipments without a 'warranty' tag, with merged tags from shipments with a 'warranty' tag
 */
function getOrderShipments(shipments) {
  if (!shipments || !Array.isArray(shipments)) {
    return []
  }

  const shipmentLength = shipments.length

  // filter out all shipments with reference_origin = null -> viu shipments do start with reference_origin = VIU_
  shipments = shipments.filter((shipment) => shipment.reference_origin !== null)

  // if shipment length was greater then 0 but no its null means the shipment creation is in progress
  if (shipments.length === 0) {
    // create a dummy shipment for each line item group
    const dummyShipments = {}

    for (const lineItem of props.order.line_items) {
      if (lineItem.reference) {
        if (!dummyShipments[lineItem.reference]) {
          dummyShipments[lineItem.reference] = {
            status: shipmentLength > 0 ? 'in_creation' : 'issue',
            line_items: [],
            shipping_method: {
              reference: props.order.metadata?.delivery_method
            }
          }
        }
        dummyShipments[lineItem.reference].line_items.push(lineItem)
      }
    }
    return Object.values(dummyShipments)
  }

  const shipmentsWithoutWarranty = shipments.filter(
    (shipment) => !shipment.tags.some((tag) => tag.name === 'warranty')
  )

  const shipmentsWithWarranty = shipments.filter((shipment) =>
    shipment.tags.some((tag) => tag.name === 'warranty')
  )

  // Create a deep copy of shipmentsWithoutWarranty to avoid modifying the original
  const updatedShipmentsWithoutWarranty = JSON.parse(JSON.stringify(shipmentsWithoutWarranty))

  // Only proceed with merging if there are warranty shipments
  if (shipmentsWithWarranty.length > 0) {
    updatedShipmentsWithoutWarranty.forEach((shipmentWithout) => {
      shipmentsWithWarranty.forEach((shipmentWith) => {
        const hasMatchingLineItem = shipmentWith.line_items.some((lineItem) =>
          shipmentWithout.line_items.some(
            (withoutLineItem) => withoutLineItem.sku_code === lineItem.sku_code
          )
        )

        if (hasMatchingLineItem) {
          // Merge tags, avoiding duplicates
          shipmentWith.tags.forEach((tag) => {
            if (!shipmentWithout.tags.some((t) => t.name === tag.name)) {
              shipmentWithout.tags.push({ ...tag })
            }
          })
        }
      })
    })
  }

  return updatedShipmentsWithoutWarranty
}

function getAsTag(shipment) {
  // return tag.name if shipment has a tag with name "intolerance" or "warranty".
  return shipment.tags?.find((tag) => tag.name === 'intolerance' || tag.name === 'insurance')?.name
}

function getVisibleItems(items) {
  return sortOrdersItems(
    items?.filter((lineItem) => {
      return (
        (lineItem.metadata?.type === 'gift_card' && lineItem.total_amount_cents > 0) ||
        (['skus'].includes(lineItem.item_type) &&
          !(
            ['lens', 'accessory', 'insurance', 'gift_card'].includes(lineItem.metadata?.type) &&
            lineItem.reference !== null
          ) &&
          lineItem.metadata?.type !== 'shipping_cost')
      )
    })
  )
}

function getPaymentColour(paymentStatus) {
  if (paymentStatus === 'paid') {
    return 'text-color-success'
  } else if (paymentStatus === 'partially_paid' || paymentStatus === 'authorized') {
    return 'text-color-danger'
  } else {
    return 'text-color-warning'
  }
}

async function showOrderPreview(id) {
  if (props.order.customer.id && props.mode === 'overview') {
    const customer = await loadCustomerById(props.order.customer.id)
    customerStore.setCustomer(customer)
  }

  customerStore.setCustomerOrderId(id)
  uiStore.showCustomerOrderDetails = true
}

function hasReturn(lineItem) {
  return lineItem?.return_line_items?.some(
    (returnItem) =>
      returnItem?.return?.status !== 'cancelled' && returnItem?.return?.status !== 'rejected'
  )
}
</script>
<style src="./CustomerOrderItem.css" scoped></style>
